import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { isReadOnly, setReadOnlyMode } from '@/lib/permissions';
import { applyAppIcons } from '@/lib/appIcons';
import { getSavedFontScale, applyFontScale } from '@/components/FontSizeSlider';
import '@/App.css';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import Branches from '@/pages/Branches';
import Mitra from '@/pages/Managers';
import Investors from '@/pages/Investors';
import Investments from '@/pages/Investments';
import Transactions from '@/pages/Transactions';
import ActivityLogs from '@/pages/ActivityLogs';
import HutangPiutang from '@/pages/HutangPiutangFinal';
import DatabaseManagement from '@/pages/DatabaseManagement';
import DatabaseEditor from '@/pages/DatabaseEditor';
import Peta from '@/pages/Peta';
import Assets from '@/pages/Assets';
import Rekapan from '@/pages/Rekapan';
import RekapanSemuaCabang from '@/pages/RekapanSemuaCabang';
import RekapanSemuaPartai from '@/pages/RekapanSemuaPartai';
import SettingsGeneral from '@/pages/SettingsGeneral';
import SettingsPeriod from '@/pages/SettingsPeriod';
import SettingsUser from '@/pages/SettingsUser';
import WhatsAppSettings from '@/pages/WhatsAppSettings';
import Layout from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';
import { BalanceSheetProvider } from '@/contexts/BalanceSheetContext';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Terapkan skala font tersimpan sedini mungkin agar tidak ada kedipan ukuran
applyFontScale(getSavedFontScale());
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('Pengepul Abg');

  // Fetch company settings (name and logo) from settings
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await api.get('/settings/public');
        const name = response.data.company_name || 'Pengepul Abg';
        setCompanyName(name);
        // Update document title
        document.title = name;
        applyAppIcons(response.data.logo_version);
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
        // Keep default if fetch fails
        document.title = 'Pengepul Abg';
      }
    };

    // Fetch company settings initially
    fetchCompanySettings();

    // Listen for settings updates
    const handleSettingsUpdate = () => {
      fetchCompanySettings();
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(res => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  // Aktifkan mode read-only (level Master 2) sebelum halaman di-render,
  // supaya seluruh tombol aksi langsung tersembunyi.
  setReadOnlyMode(isReadOnly(user));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BalanceSheetProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
          <Route path="/register" element={!user ? <Register onLogin={handleLogin} /> : <Navigate to="/" />} />
          <Route path="/" element={user ? <Layout user={user} onLogout={handleLogout} companyName={companyName} /> : <Navigate to="/login" />}>
            <Route index element={<Dashboard user={user} />} />
            <Route path="branches" element={<Branches user={user} />} />
            <Route path="mitra" element={<Mitra user={user} />} />
            <Route path="investors" element={<Investors user={user} />} />
            <Route path="investments" element={<Investments user={user} />} />
            <Route path="transactions" element={<Transactions user={user} />} />
            <Route path="peta" element={<Peta user={user} />} />
            <Route path="assets" element={<Assets key="rumah" user={user} initialTab="rumah" />} />
            <Route path="assets/rumah" element={<Assets key="rumah" user={user} initialTab="rumah" />} />
            <Route path="assets/mobil" element={<Assets key="mobil" user={user} initialTab="mobil" />} />
            <Route path="rekapan" element={<Rekapan user={user} />} />
            <Route path="rekapan-semua-cabang" element={<RekapanSemuaCabang user={user} />} />
            <Route path="rekapan-semua-partai" element={<RekapanSemuaPartai user={user} />} />
            <Route path="hutang-piutang" element={<HutangPiutang user={user} />} />
            <Route path="activity-logs" element={<ActivityLogs user={user} />} />
            <Route path="database-management" element={<DatabaseManagement user={user} />} />
            <Route path="database-editor" element={<DatabaseEditor user={user} />} />
            <Route path="pengaturan/general" element={<SettingsGeneral user={user} />} />
            <Route path="pengaturan/periode" element={<SettingsPeriod user={user} />} />
            <Route path="pengaturan/user" element={<SettingsUser user={user} />} />
            <Route path="pengaturan/whatsapp" element={<WhatsAppSettings user={user} />} />
          </Route>
        </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </BalanceSheetProvider>
    </div>
  );
}

export default App;
